import { DUPE_API_URL } from '@dupe/common/constants'
import { useQuery } from '@tanstack/react-query'
import useAuthUser from '~/src/hooks/useAuthUser'
import fetcher from '~/src/lib/fetcher'

export type ProfileResult = {
  hash: string
  image: string
  source?: string
}

export type ProfileBookmark = {
  cartItemId: string
  image: string
  url: string
  /** The shop it's from, as the api names it ("Nordstrom"). */
  source?: string
  title?: string | null
  pricing?: { price?: number | null; currency?: string | null } | null
  /** furniture, fashion or fragrance, when the api could place it (the Library filters). */
  category?: string | null
}

export type ProfileData = {
  results: ProfileResult[]
  bookmarks: ProfileBookmark[]
}

interface UseFetchProfileDataProps {
  limit?: number
  /** How many saves to read (the api's default is 15, at most 50). */
  bookmarksLimit?: number
  /** Off: no request (the header's Library menu while its flag is off). */
  enabled?: boolean
}

/**
 * Hook to fetch profile data including recent finds and bookmarks
 */
const useFetchProfileData = (props?: UseFetchProfileDataProps) => {
  const { limit = 50, bookmarksLimit, enabled = true } = props || {}
  const { authUser } = useAuthUser()

  return useQuery<ProfileData>({
    queryKey: ['profile-data', authUser?.uid, bookmarksLimit],
    queryFn: async () => {
      const respData = await fetcher<ProfileData>(
        `${DUPE_API_URL}/api/profile`,
        {
          method: 'GET',
          dataOrParams: bookmarksLimit ? { limit, bookmarksLimit } : { limit },
          authUser,
        }
      )

      return respData
    },
    enabled: enabled && !!authUser?.uid,
  })
}

export default useFetchProfileData
